import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../Icon';
import {
  FunnelWelcomeFrame,
  welcomeContainerVariants,
  welcomeItemVariants,
} from '../../pages/FunnelWelcomePage';

/**
 * The "question-first" opening of the web2app funnels (see
 * hooks/useWelcomeVariant.js): one tap on the screen-time struggle that
 * brought the parent here, before Pixley asks for anything.
 *
 * The question and its four answers are the ones the original /funnel-auth
 * asked (its retired 'concern' step), so the `screen_concern` values on the
 * events line up with what that version collected. The answer is analytics
 * only: it goes on the funnel's PostHog events and nowhere near the account.
 *
 * Two homes, one option grid:
 *  - <FunnelQuestionPage>: a full-page takeover in the welcome screen's frame
 *    (art, logo, dark panel) — what /funnel-auth shows in place of the welcome.
 *  - <FunnelQuestionStep>: the same question inside a funnel step card — what
 *    /funnel-auth/intro shows after its explainer slides.
 */

export const SCREEN_TIME_QUESTION = {
  eyebrow: 'One quick question',
  // Rendered as two lines, like the welcome screen's title: a big first line
  // and a smaller accented second
  title: "What's your biggest",
  titleAccent: 'struggle with screen time?',
  subtitle: 'Pixley turns screen time into stories that are actually good for them.',
  options: [
    { value: 'mindless-watching', icon: 'smartphone', tint: 'blue', label: 'Mindless watching' },
    // Non-breaking hyphen: wraps "Screen-time / battles", never "Screen- / time"
    { value: 'screen-battles', icon: 'sentiment_stressed', tint: 'coral', label: 'Screen‑time battles' },
    { value: 'not-learning', icon: 'psychology', tint: 'purple', label: 'Not learning anything' },
    { value: 'unsafe-content', icon: 'shield', tint: 'green', label: 'Unsafe content' },
  ],
};

export const QUESTION_VALUES = SCREEN_TIME_QUESTION.options.map((o) => o.value);
export const QUESTION_BY_VALUE = Object.fromEntries(
  SCREEN_TIME_QUESTION.options.map((o) => [o.value, o])
);

// How long the chosen card gets to land its bounce before the funnel moves
// on — the tap should feel like it did it, with just enough of a beat to see
// what was picked.
export const QUESTION_ADVANCE_MS = 520;

// ── motion ──────────────────────────────────────────────────────────────────
// Each card drives its own animation from the start (framer ignores an
// `animate` that only shows up after a child has been inheriting its parent's
// variants): it pops in on a short per-card delay, so the four fan in one
// after another, and from the moment one is picked it's told where it stands —
// the pick bounces, the rest fall back.
const POP = { type: 'spring', stiffness: 420, damping: 26 };
const cardVariants = {
  enter: { opacity: 0, y: 18, scale: 0.92 },
  center: (i) => ({ opacity: 1, y: 0, scale: 1, transition: { ...POP, delay: 0.3 + i * 0.07 } }),
  picked: { opacity: 1, y: 0, scale: [1, 1.08, 1], transition: { duration: 0.42, times: [0, 0.4, 1], ease: 'easeOut' } },
  passed: { opacity: 0.38, y: 0, scale: 0.94, transition: { duration: 0.28 } },
};
const iconVariants = {
  enter: { rotate: 0, scale: 1 },
  center: { rotate: 0, scale: 1 },
  picked: { rotate: [0, -10, 8, 0], scale: [1, 1.18, 1.1, 1.1], transition: { duration: 0.5, ease: 'easeOut' } },
  passed: { rotate: 0, scale: 1 },
};
const checkVariants = {
  enter: { opacity: 0, scale: 0 },
  center: { opacity: 0, scale: 0 },
  picked: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 520, damping: 18, delay: 0.08 } },
  passed: { opacity: 0, scale: 0 },
};

/**
 * The tappable answers, a 2×2 grid of cards: icon tile over a label, both
 * centred, every card the same height so the grid reads as one block. `tone`
 * is 'dark' on the takeover (cards sit on the night-blue panel) and 'light'
 * inside a white step card.
 */
export const FunnelQuestionOptions = ({
  question = SCREEN_TIME_QUESTION,
  selected,
  onSelect,
  tone = 'light',
}) => (
  <div
    className={`fq-grid fq-${tone}`}
    role="group"
    aria-label={`${question.title} ${question.titleAccent}`}
  >
    {question.options.map((opt, i) => {
      const isPicked = selected === opt.value;
      let state = 'center';
      if (selected) state = isPicked ? 'picked' : 'passed';
      return (
        <motion.button
          key={opt.value}
          type="button"
          className={`fq-card fq-tint-${opt.tint}${isPicked ? ' selected' : ''}`}
          aria-pressed={isPicked}
          disabled={!!selected && !isPicked}
          onClick={() => onSelect(opt.value)}
          custom={i}
          variants={cardVariants}
          initial="enter"
          animate={state}
          whileHover={selected ? undefined : { y: -3, scale: 1.02 }}
          whileTap={selected ? undefined : { scale: 0.94 }}
          transition={POP}
        >
          <motion.span className="fq-icon" aria-hidden="true" variants={iconVariants}>
            <Icon name={opt.icon} size={26} fill />
          </motion.span>
          <strong className="fq-label">{opt.label}</strong>
          <motion.span className="fq-check" aria-hidden="true" variants={checkVariants}>
            <Icon name="check" size={14} />
          </motion.span>
        </motion.button>
      );
    })}
  </div>
);

/** Full-page takeover: the welcome screen's frame with the question in it. */
export const FunnelQuestionPage = ({ question = SCREEN_TIME_QUESTION, selected, onSelect }) => (
  <FunnelWelcomeFrame>
    <FunnelQuestionStyles />
    <motion.section
      className="fw-panel fq-panel"
      variants={welcomeContainerVariants}
      initial="enter"
      animate="center"
      aria-labelledby="fq-title"
    >
      <motion.div className="fq-eyebrow" variants={welcomeItemVariants}>{question.eyebrow}</motion.div>
      <motion.h1 id="fq-title" className="fq-title" variants={welcomeItemVariants}>
        <span>{question.title}</span>
        <span className="fq-title-accent">{question.titleAccent}</span>
      </motion.h1>
      <motion.p className="fq-subtitle" variants={welcomeItemVariants}>{question.subtitle}</motion.p>
      <FunnelQuestionOptions question={question} selected={selected} onSelect={onSelect} tone="dark" />
    </motion.section>
  </FunnelWelcomeFrame>
);

/** Inside a funnel step card (FunnelScreens' fs-* typography). */
export const FunnelQuestionStep = ({ question = SCREEN_TIME_QUESTION, selected, onSelect, itemVariants }) => (
  <>
    <FunnelQuestionStyles />
    <motion.div variants={itemVariants} className="fs-eyebrow">{question.eyebrow}</motion.div>
    <motion.h1 variants={itemVariants} className="fs-title">
      {question.title}<br /><span className="fs-accent">{question.titleAccent}</span>
    </motion.h1>
    <motion.p variants={itemVariants} className="fs-sub">{question.subtitle}</motion.p>
    <FunnelQuestionOptions question={question} selected={selected} onSelect={onSelect} tone="light" />
  </>
);

export const FunnelQuestionStyles = () => (
  <style>{`
    /* ── the grid of answer cards (both tones) ─────────────────────────── */
    .fq-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      width: 100%;
    }
    /* Every card is laid out identically — icon tile, then a two-line label
       box — so the four line up whatever their copy wraps to. Motion is
       framer's (see the variants above); CSS only colours the states. */
    .fq-card {
      position: relative;
      display: flex; flex-direction: column; align-items: center;
      min-width: 0;
      padding: 14px 10px 12px;
      border-radius: 20px;
      cursor: pointer;
      text-align: center;
      font-family: 'Inter', sans-serif;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: border-color 0.2s, background 0.2s, box-shadow 0.25s;
    }
    .fq-card:disabled { cursor: default; }
    .fq-card:focus-visible { outline: 3px solid rgba(125,187,255,0.6); outline-offset: 2px; }
    .fq-icon {
      width: 44px; height: 44px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 14px;
      margin-bottom: 8px;
    }
    .fq-label {
      display: flex; align-items: center; justify-content: center;
      min-height: 2.4em;
      font-size: 15px; font-weight: 700; line-height: 1.2;
      text-wrap: balance;
    }
    .fq-check {
      position: absolute; top: 9px; right: 9px;
      width: 22px; height: 22px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      color: #FFF;
      background: #0A6CFF;
    }

    /* Icon tile colours — one per answer, so the grid reads as four things
       rather than one list */
    .fq-tint-blue .fq-icon   { color: #1F74FD; background: #E3EEFF; }
    .fq-tint-coral .fq-icon  { color: #FF4F8B; background: #FFE4EE; }
    .fq-tint-purple .fq-icon { color: #7C5CFF; background: #ECE7FF; }
    .fq-tint-green .fq-icon  { color: #1FA84A; background: #DDF6E4; }

    /* ── light: inside a white step card ───────────────────────────────── */
    .fq-light .fq-card {
      background: #F7FAFF;
      border: 2px solid #E8EDF5;
    }
    .fq-light .fq-card:hover:not(:disabled):not(.selected) {
      border-color: rgba(10,108,255,0.45);
      background: #FFF;
      box-shadow: 0 10px 22px rgba(10,108,255,0.12);
    }
    .fq-light .fq-card.selected {
      border-color: #0A6CFF;
      background: #F0F6FF;
      box-shadow: 0 10px 26px rgba(10,108,255,0.22);
    }
    .fq-light .fq-label { color: #0B1A38; }

    /* ── dark: on the welcome frame's night-blue panel ─────────────────── */
    /* Centred like the welcome screen's copy, which this replaces */
    .fq-panel {
      padding-top: 16cqw;
      padding-bottom: calc(9cqw + env(safe-area-inset-bottom, 0px));
      align-items: center;
      text-align: center;
      /* The panel reaches higher up the art than the welcome copy does, so
         it brings its own fade for the title to sit on */
      background: linear-gradient(180deg, rgba(10,20,50,0) 0%, rgba(10,20,50,0.8) 12cqw, #0A1432 26cqw);
    }
    .fq-eyebrow {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 3.3cqw; font-weight: 700;
      letter-spacing: 0.16em; text-transform: uppercase;
      color: #7DB6FF;
      margin: 0 0 1.6cqw;
      text-shadow: 0 2px 10px rgba(3,12,34,0.6);
    }
    .fq-title {
      margin: 0;
      font-size: 10cqw;
      font-weight: 700;
      line-height: 1.05;
      letter-spacing: -0.025em;
      color: #FFF;
      text-shadow: 0 3px 16px rgba(3,12,34,0.55);
    }
    .fq-title > span { display: block; }
    .fq-title-accent {
      font-size: 0.7em;
      line-height: 1.2;
      background: linear-gradient(180deg, #7DBBFF 0%, #4A93FF 100%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      text-shadow: none;
      filter: drop-shadow(0 3px 12px rgba(3,12,34,0.5));
    }
    .fq-subtitle {
      margin: 2.4cqw 0 4.6cqw;
      font-family: 'Inter', 'Fredoka', sans-serif;
      font-size: 3.9cqw;
      font-weight: 400;
      line-height: 1.4;
      color: rgba(255,255,255,0.86);
      text-shadow: 0 2px 10px rgba(3,12,34,0.6);
      text-wrap: balance;
    }
    .fq-dark { gap: 2.6cqw; }
    .fq-dark .fq-card {
      padding: 3.6cqw 2.6cqw 3cqw;
      border-radius: 5.4cqw;
      background: rgba(255,255,255,0.09);
      border: 1.5px solid rgba(255,255,255,0.16);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      box-shadow: 0 8px 24px rgba(3,12,34,0.25);
    }
    .fq-dark .fq-card:hover:not(:disabled):not(.selected) {
      background: rgba(255,255,255,0.15);
      border-color: rgba(125,187,255,0.55);
    }
    .fq-dark .fq-card.selected {
      background: rgba(31,116,253,0.4);
      border-color: #7DBBFF;
      box-shadow: 0 14px 34px rgba(31,116,253,0.5), 0 0 0 4px rgba(125,187,255,0.18), inset 0 1px 0 rgba(255,255,255,0.25);
    }
    .fq-dark .fq-icon { width: 11cqw; height: 11cqw; border-radius: 3.6cqw; margin-bottom: 2cqw; }
    .fq-dark .fq-icon .material-symbols-outlined { font-size: 6.4cqw !important; }
    .fq-dark .fq-label { color: #FFF; font-size: 4cqw; }
    .fq-dark .fq-check { top: 2.4cqw; right: 2.4cqw; width: 5.6cqw; height: 5.6cqw; background: #FFF; color: #1F74FD; }

    /* Short phones: trim the panel so all four cards stay above the fold */
    @media (max-height: 700px) and (max-aspect-ratio: 3/4) {
      .fq-panel { padding-bottom: calc(6cqw + env(safe-area-inset-bottom, 0px)); }
      .fq-title { font-size: 9cqw; }
      .fq-subtitle { margin: 1.8cqw 0 3.2cqw; font-size: 3.6cqw; }
      .fq-dark .fq-card { padding: 2.8cqw 2.6cqw 2.4cqw; }
      .fq-dark .fq-icon { width: 9.4cqw; height: 9.4cqw; margin-bottom: 1.4cqw; }
      .fq-dark .fq-label { min-height: 0; }
    }

    @media (prefers-reduced-motion: reduce) {
      .fq-card { transition: none; }
    }
  `}</style>
);
